'use client'

import { usePathname } from 'next/navigation'
import React, { useEffect, useSyncExternalStore } from 'react'

import { useCart } from '@/app/book/hooks/useCart'
import { useStep } from '@/app/book/hooks/useStep'
import { useMounted, usePageLoad } from '@/hooks/usePageLoad'
import {
  isRudderstackLoaded,
  loadRudderstackOnce,
  rudderModuleRef,
  subscribeToRudderstackLoaded,
  trackPageViewOnce,
} from '@/hooks/useRudderstackAnalytics'
import useAnonymousIdStore from '@/stores/useAnonymousIdStore'
import useSessionStore from '@/stores/useSessionStore'

/**
 * Sole owner of RudderStack lifecycle side effects: SDK load, page views
 * (hard loads AND client-side navigations), identify, and publishing the
 * anonymousId for attribution capture. Mounted exactly once in AppProviders;
 * renders nothing. Every useRudderstackAnalytics() call site is a pure
 * consumer — see the module notes in src/hooks/useRudderstackAnalytics.ts.
 */
const RudderstackAnalyticsBootstrap: React.FC = () => {
  const mounted = useMounted()
  const pageLoaded = usePageLoad()
  const pathname = usePathname()
  const loaded = useSyncExternalStore(
    subscribeToRudderstackLoaded,
    isRudderstackLoaded,
    () => false,
  )
  const setAnonymousId = useAnonymousIdStore((store) => store.setAnonymousId)
  const userEmail = useSessionStore((store) => store.userEmail)
  const userId = useSessionStore((store) => store.userId)
  const userRoles = useSessionStore((store) => store.userRoles)
  const { cart } = useCart()
  const { name, number } = useStep()

  // Load once after hydration + window 'load'.
  useEffect(() => {
    if (mounted && pageLoaded) loadRudderstackOnce()
  }, [mounted, pageLoaded])

  // Track page views on load and on every pathname change.
  useEffect(() => {
    if (loaded && pathname) trackPageViewOnce(pathname, { name, number })
  }, [loaded, pathname, name, number])

  // Identify user.
  useEffect(() => {
    if (!loaded || !userId) return

    rudderModuleRef.current?.identify(userId, {
      cart_id: cart?.id,
      email: userEmail ?? undefined,
      roles: userRoles,
    })
  }, [cart?.id, loaded, userEmail, userId, userRoles])

  // Publish RudderStack's anonymousId so attribution capture can reuse it as
  // the stitch key without spinning up a second RudderStack instance.
  useEffect(() => {
    if (!loaded) return

    const anonymousId = rudderModuleRef.current?.getAnonymousId?.()
    if (anonymousId) setAnonymousId(anonymousId)
  }, [loaded, setAnonymousId])

  return null
}

export default RudderstackAnalyticsBootstrap
